import React, { useEffect, useMemo, useState } from 'react';
import { MdLocalHospital } from 'react-icons/md';
import { ipApi } from '../../services/api';
import { Q } from '../../config/queueStatus';

/**
 * Fullscreen waiting-room TV display.
 * Prefer /display/waiting-room?hospitalId=…&displayKey=… (dedicated kiosk).
 * Same-browser staff session also works as a fallback.
 */
function statusColor(s) {
  if (s === Q.WAITING_DOCTOR) return '#10b981';
  if (s === Q.IN_CONSULTATION) return '#8b5cf6';
  if (s === Q.CHECKED_IN || s === Q.AWAITING_PAYMENT) return '#f59e0b';
  return '#94a3b8';
}

function statusLabel(s) {
  if (s === Q.WAITING_DOCTOR) return 'Waiting';
  if (s === Q.IN_CONSULTATION) return 'Now consulting';
  if (s === Q.CHECKED_IN) return 'Checked-in';
  if (s === Q.AWAITING_PAYMENT) return 'At billing';
  return s || '';
}

export default function QueueTvDisplay() {
  const [queue, setQueue] = useState([]);
  const [hospitalName, setHospitalName] = useState('');
  const [clock, setClock] = useState(new Date());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const hospitalId = params.get('hospitalId') || '';
  const displayKey = params.get('displayKey') || '';

  useEffect(() => {
    // Never keep staff Bearer tokens in the address bar
    if (params.get('token')) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('token');
      window.history.replaceState({}, '', clean.pathname + clean.search);
    }
  }, [params]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        let rows = [];
        let name = '';
        if (hospitalId && displayKey) {
          const data = await ipApi.getWaitingRoomDisplay(hospitalId, displayKey);
          rows = data?.queue || [];
          name = data?.hospitalName || '';
        } else {
          const data = await ipApi.getQueue({ limit: 150 });
          const today = new Date().toISOString().slice(0, 10);
          rows = (data?.queue || []).filter((q) => {
            const d = (q.visitDate || (q.createdAt || '').slice(0, 10));
            return !d || d === today;
          });
          if (data?.hospitalName) name = data.hospitalName;
        }
        if (cancelled) return;
        setQueue(rows);
        if (name) setHospitalName(name);
        setError('');
      } catch (e) {
        if (!cancelled) {
          setError(
            e.message
            || (displayKey
              ? 'Could not load waiting room — check display key'
              : 'Sign in on this browser, or open TV Screen from Reception (uses a display key).'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const poll = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [hospitalId, displayKey]);

  useEffect(() => {
    if (displayKey) return;
    ipApi.getHospitalProfile().then((p) => {
      if (p?.hospital?.name) setHospitalName(p.hospital.name);
    }).catch(() => {});
  }, [displayKey]);

  const waiting = queue.filter((q) => [Q.WAITING_DOCTOR, Q.CHECKED_IN, Q.AWAITING_PAYMENT, Q.IN_CONSULTATION].includes(q.status)
    || ['Waiting for Doctor', 'Checked In', 'Awaiting Payment', 'In Consultation'].includes(q.status));
  const nowServing = waiting.filter((q) => q.status === Q.IN_CONSULTATION || q.status === 'In Consultation');
  const nextUp = waiting.filter((q) => q.status !== Q.IN_CONSULTATION && q.status !== 'In Consultation');

  return (
    <div style={{
      minHeight: '100vh', background: '#0f172a', color: '#f8fafc', padding: '24px 32px',
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MdLocalHospital size={36} color="#38bdf8" />
          <div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{hospitalName || 'Waiting Room'}</div>
            <div style={{ opacity: 0.7, fontSize: 14 }}>Live queue</div>
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
          {clock.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      </div>

      {loading && <p style={{ opacity: 0.7 }}>Loading…</p>}
      {error && <p style={{ color: '#fbbf24', marginBottom: 16 }}>{error}</p>}

      {nowServing.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 14, letterSpacing: 1, opacity: 0.6, marginBottom: 10 }}>NOW CONSULTING</div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {nowServing.map((q) => (
              <div key={q.queueId} style={{ background: '#1e1b4b', borderRadius: 12, padding: 20, border: '2px solid #8b5cf6' }}>
                <div style={{ fontSize: 42, fontWeight: 800 }}>{q.token || '—'}</div>
                <div style={{ fontSize: 18, marginTop: 4 }}>{q.patientName || q.name}</div>
                <div style={{ opacity: 0.7, marginTop: 6 }}>{q.doctor}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 14, letterSpacing: 1, opacity: 0.6, marginBottom: 10 }}>WAITING</div>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        {nextUp.map((q) => (
          <div key={q.queueId} style={{
            background: '#1e293b', borderRadius: 10, padding: 16,
            borderLeft: `4px solid ${statusColor(q.status)}`,
          }}>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{q.token || '—'}</div>
            <div style={{ marginTop: 4 }}>{q.patientName || q.name}</div>
            <div style={{ fontSize: 13, opacity: 0.65, marginTop: 4 }}>{q.doctor} · {statusLabel(q.status)}</div>
          </div>
        ))}
        {!loading && !error && nextUp.length === 0 && nowServing.length === 0 && (
          <p style={{ opacity: 0.6 }}>No patients in queue right now.</p>
        )}
      </div>
    </div>
  );
}
